import { ComponentType, CoolantFluid, CircuitTemplate, SimulationResult, RecommendationResult, CircuitSchema, CircuitNode } from '../types';

export interface UserProfile {
  company: string;
  phone: string;
  job_title: string;
  avatar: string | null;
  preferred_units: 'si' | 'imperial';
  onboarding_completed: boolean;
  email_verified: boolean;
  created_at: string;
  updated_at: string;
}

export interface AuthUser {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  is_staff: boolean;
  is_active: boolean;
  date_joined: string;
  last_login: string | null;
  profile: UserProfile;
  // Legacy compat
  username?: string;
}

export interface AuthResponse {
  user: AuthUser;
  csrftoken: string;
}

export interface WorkspaceManifest {
  format: string;
  version: number;
  exported_at: string;
  scope: string[];
  counts: Record<string, number>;
  record_count: number;
  media_files: number;
  media_missing: string[];
  database_engine: string;
}

export interface WorkspaceImportResult {
  manifest: WorkspaceManifest;
  dry_run?: boolean;
  loaded?: Record<string, number>;
  skipped?: Record<string, number>;
  deleted?: Record<string, number>;
  media_restored?: number;
  record_count?: number;
}

export interface AgentStatus {
  enabled: boolean;
  configured: boolean;
  requires_login: boolean;
  model: string | null;
  /** OpenRouter's Auto Router picks the model per turn. */
  auto_routing?: boolean;
}

export interface AgentAttachment {
  name: string;
  /** base64 data URL (image/* or application/pdf) */
  data: string;
}

export interface AgentChatRequest {
  message: string;
  schema: CircuitSchema;
  history: { role: 'user' | 'assistant'; content: string }[];
  attachments?: AgentAttachment[];
}

/** Condensed result of one simulated candidate (see backend optimization.Metrics). */
export interface CandidateMetrics {
  feasible: boolean;
  converged: boolean;
  error?: string;
  min_margin?: number;
  peak_temperature?: number;
  min_flow?: number;
  max_flow?: number;
  max_pressure_drop?: number;
  worst?: string;
  violations?: string[];
  warnings?: string[];
  /** optimize_design: variable label -> chosen value */
  settings?: Record<string, string>;
  /** compare_topologies: e.g. "Battery → (Motor ‖ Inverter)" */
  layout?: string;
  arrangement?: string;
}

export interface OptimizationReport {
  objective: string;
  objective_description: string;
  baseline: CandidateMetrics;
  best: CandidateMetrics;
  top?: CandidateMetrics[];
  ranking?: CandidateMetrics[];
  evaluations?: number;
  evaluated?: number;
  stopped_early?: string | null;
  applied: boolean;
  note?: string;
}

export interface AgentAction {
  tool: string;
  arguments: any;
  ok: boolean;
  error?: string;
  mutating: boolean;
  /** Present for optimize_design / compare_topologies. */
  report?: OptimizationReport;
}

/** A running or finished assistant turn, as polled from /agent/jobs/<id>/. */
export interface AgentJobState {
  job_id: string;
  status: 'running' | 'done' | 'error';
  progress: string[];
  elapsed: number;
  result?: AgentChatResponse;
  error?: string;
  error_status?: number;
}

export interface AgentChatResponse {
  reply: string;
  changed: boolean;
  schema: CircuitSchema | null;
  actions: AgentAction[];
  simulation: SimulationResult[] | null;
  model: string;
  /** Models that actually answered in this turn (differs from `model` with auto routing). */
  models_used?: string[];
  usage: { prompt_tokens: number; completion_tokens: number };
}

export interface Project {
  id: number;
  name: string;
  description: string;
  is_archived: boolean;
  circuit_count: number;
  created_at: string;
  updated_at: string;
}

export interface SavedCircuit {
  id: number;
  share_id: string;
  title: string;
  description: string;
  circuit_schema: any;
  is_public: boolean;
  thumbnail: string | null;
  last_simulation_at: string | null;
  owner: number | null;
  owner_email: string | null;
  project: number | null;
  created_at: string;
  updated_at: string;
}

export interface CircuitShareRecord {
  id: number;
  circuit: number;
  shared_with: number;
  shared_with_email: string;
  shared_by: number;
  shared_by_email: string;
  permission: 'view' | 'edit' | 'duplicate';
  created_at: string;
}

export interface UserComponent {
  id: number;
  name: string;
  component_type: string;
  component_type_display: string;
  description: string;
  default_properties: Record<string, any>;
  curve_data: any[];
  source_file: string | null;
  icon: string;
  is_active: boolean;
  owner: number;
  owner_email: string;
  created_at: string;
  updated_at: string;
}

export interface SimulationRunRecord {
  id: number;
  user: number;
  circuit: number | null;
  circuit_title?: string | null;
  scenario_name: string;
  input_params?: any;
  results?: any;
  status: 'success' | 'warning' | 'error';
  warnings?: string[];
  duration_ms: number;
  created_at: string;
}

export interface AdminUser {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  company: string;
  is_active: boolean;
  is_staff: boolean;
  date_joined: string;
  last_login: string | null;
  circuit_count: number;
  simulation_count: number;
  profile?: UserProfile;
  custom_component_count?: number;
}

export interface AnalyticsSummary {
  total_rfqs: number;
  rfqs_by_status: Record<string, number>;
  total_simulations: number;
  total_recommendations: number;
  total_pdf_exports: number;
  total_shared_circuits: number;
  recent_rfqs: RfqSubmission[];
  component_counts: Record<string, number>;
  components_with_curves: Record<string, number>;
}

export interface RfqSubmission {
  id: number;
  name: string;
  company: string;
  email: string;
  phone: string;
  message: string;
  circuit_schema: any;
  simulation_summary: any;
  circuit_share_url: string;
  status: string;
  notes: string;
  created_at: string;
}

class ApiService {
  private baseURL: string;

  constructor() {
    this.baseURL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
  }

  // ── Workspace transfer (superuser only) ──────────────────────────────

  /** Downloads a workspace archive and hands it to the browser to save. */
  async downloadWorkspaceExport(includeUserData: boolean): Promise<void> {
    const query = includeUserData ? '?include_user_data=1' : '';
    const response = await fetch(
      `${this.baseURL}/admin-api/workspace/export/${query}`,
      { credentials: 'include' },
    );

    if (!response.ok) {
      const body = await response.text().catch(() => '');
      throw new Error(`HTTP ${response.status}: ${body}`);
    }

    const blob = await response.blob();
    const disposition = response.headers.get('Content-Disposition') || '';
    const match = disposition.match(/filename="([^"]+)"/);
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = match ? match[1] : 'workspace-export.zip';
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }

  async importWorkspace(
    file: File,
    options: { includeUserData?: boolean; replace?: boolean; dryRun?: boolean } = {},
  ): Promise<WorkspaceImportResult> {
    const formData = new FormData();
    formData.append('archive', file);
    if (options.includeUserData) formData.append('include_user_data', '1');
    if (options.replace) formData.append('replace', '1');
    if (options.dryRun) formData.append('dry_run', '1');

    const response = await fetch(
      `${this.baseURL}/admin-api/workspace/import/`,
      {
        method: 'POST',
        body: formData,
        credentials: 'include',
        headers: { 'X-CSRFToken': this.getCsrfToken() },
      },
    );

    const body = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(body.error || `HTTP ${response.status}`);
    }
    return body;
  }

  private getCsrfToken(): string {
    const match = document.cookie.match(/csrftoken=([^;]+)/);
    return match ? match[1] : '';
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseURL}${endpoint}`;

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string> || {}),
    };

    // Add CSRF token for non-GET requests
    if (options.method && options.method !== 'GET') {
      headers['X-CSRFToken'] = this.getCsrfToken();
    }

    const config: RequestInit = {
      ...options,
      headers,
      credentials: 'include',
    };

    const response = await fetch(url, config);

    if (!response.ok) {
      const errorBody = await response.text().catch(() => '');
      throw new Error(`HTTP ${response.status}: ${errorBody}`);
    }

    return await response.json();
  }

  // --- Components ---

  async getComponents(): Promise<ComponentType[]> {
    return this.request<ComponentType[]>('/components/');
  }

  /**
   * Add a component defined on the canvas to the shared library (admins only).
   * Its custom curve becomes the library model's characteristic.
   */
  async addComponentFromCanvas(
    node: CircuitNode,
    meta: { name: string; manufacturer?: string; description?: string },
  ): Promise<ComponentType> {
    const response = await fetch(`${this.baseURL}/components/from-canvas/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-CSRFToken': this.getCsrfToken() },
      credentials: 'include',
      body: JSON.stringify({ node: { id: node.id, type: node.type, data: node.data }, ...meta }),
    });
    const body = await response.json().catch(() => null);
    if (!response.ok) {
      throw new Error(body?.error || body?.detail || `Could not add the component (HTTP ${response.status}).`);
    }
    return body as ComponentType;
  }

  async getComponentsByType(type: string): Promise<ComponentType[]> {
    return this.request<ComponentType[]>(`/components/?type=${type}`);
  }

  async getComponent(id: number): Promise<ComponentType> {
    return this.request<ComponentType>(`/components/${id}/`);
  }

  async createComponent(data: Partial<ComponentType>): Promise<ComponentType> {
    return this.request<ComponentType>('/components/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateComponent(id: number, data: Partial<ComponentType>): Promise<ComponentType> {
    return this.request<ComponentType>(`/components/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteComponent(id: number): Promise<void> {
    await this.request(`/components/${id}/`, { method: 'DELETE' });
  }

  async duplicateComponent(id: number): Promise<ComponentType> {
    return this.request<ComponentType>(`/components/${id}/duplicate/`, {
      method: 'POST',
    });
  }

  async getCurveData(componentId: number, volumeFlow?: number): Promise<any[]> {
    const qs = volumeFlow !== undefined ? `?volume_flow=${volumeFlow}` : '';
    return this.request<any[]>(`/components/${componentId}/curve_data/${qs}`);
  }

  async createCurve(data: { component: number; curve_type: string; data: Record<string, number>[]; metadata?: Record<string, any> }): Promise<any> {
    return this.request<any>('/curves/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateCurve(curveId: number, data: { data: Record<string, number>[]; metadata?: Record<string, any> }): Promise<any> {
    return this.request<any>(`/curves/${curveId}/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteCurve(curveId: number): Promise<void> {
    await this.request(`/curves/${curveId}/`, { method: 'DELETE' });
  }

  async uploadComponentData(componentId: number, file: File): Promise<any> {
    const formData = new FormData();
    formData.append('file', file);

    const url = `${this.baseURL}/components/${componentId}/upload_data/`;
    const response = await fetch(url, {
      method: 'POST',
      body: formData,
      credentials: 'include',
      headers: {
        'X-CSRFToken': this.getCsrfToken(),
      },
    });

    if (!response.ok) {
      const errorBody = await response.text().catch(() => '');
      throw new Error(`HTTP ${response.status}: ${errorBody}`);
    }

    return await response.json();
  }

  async downloadTemplate(componentId: number): Promise<Blob> {
    const url = `${this.baseURL}/components/${componentId}/download_template/`;
    const response = await fetch(url, { credentials: 'include' });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return await response.blob();
  }

  // --- Coolant Fluids ---

  async getFluids(): Promise<CoolantFluid[]> {
    return this.request<CoolantFluid[]>('/fluids/');
  }

  async getFluid(id: number): Promise<CoolantFluid> {
    return this.request<CoolantFluid>(`/fluids/${id}/`);
  }

  async createFluid(data: Partial<CoolantFluid>): Promise<CoolantFluid> {
    return this.request<CoolantFluid>('/fluids/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateFluid(id: number, data: Partial<CoolantFluid>): Promise<CoolantFluid> {
    return this.request<CoolantFluid>(`/fluids/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteFluid(id: number): Promise<void> {
    await this.request(`/fluids/${id}/`, { method: 'DELETE' });
  }

  // --- Circuit Templates ---

  async getTemplates(): Promise<CircuitTemplate[]> {
    return this.request<CircuitTemplate[]>('/templates/');
  }

  async getTemplate(id: number): Promise<CircuitTemplate> {
    return this.request<CircuitTemplate>(`/templates/${id}/`);
  }

  async createTemplate(data: Partial<CircuitTemplate>): Promise<CircuitTemplate> {
    return this.request<CircuitTemplate>('/templates/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateTemplate(id: number, data: Partial<CircuitTemplate>): Promise<CircuitTemplate> {
    return this.request<CircuitTemplate>(`/templates/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteTemplate(id: number): Promise<void> {
    await this.request(`/templates/${id}/`, { method: 'DELETE' });
  }

  // --- Simulation ---

  async simulate(schema: CircuitSchema): Promise<SimulationResult[]> {
    return this.request<SimulationResult[]>('/simulate/', {
      method: 'POST',
      body: JSON.stringify(schema),
    });
  }

  async recommend(schema: CircuitSchema): Promise<RecommendationResult[]> {
    return this.request<RecommendationResult[]>('/simulate/recommend/', {
      method: 'POST',
      body: JSON.stringify(schema),
    });
  }

  // --- RFQ ---

  async submitRfq(data: {
    name: string;
    company: string;
    email: string;
    phone?: string;
    message?: string;
    circuit_schema: any;
  }): Promise<any> {
    return this.request<any>('/rfq/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // --- RFQ Admin ---

  async getRfqs(params?: { status?: string; search?: string }): Promise<RfqSubmission[]> {
    const query = new URLSearchParams();
    if (params?.status) query.set('status', params.status);
    if (params?.search) query.set('search', params.search);
    const qs = query.toString();
    return this.request<RfqSubmission[]>(`/rfq-admin/${qs ? '?' + qs : ''}`);
  }

  async getRfq(id: number): Promise<RfqSubmission> {
    return this.request<RfqSubmission>(`/rfq-admin/${id}/`);
  }

  async updateRfq(id: number, data: Partial<RfqSubmission>): Promise<RfqSubmission> {
    return this.request<RfqSubmission>(`/rfq-admin/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteRfq(id: number): Promise<void> {
    await this.request(`/rfq-admin/${id}/`, { method: 'DELETE' });
  }

  // --- PDF Export ---

  async exportPdf(data: { schema: any; simulation_results?: any[]; circuit_image?: string }): Promise<Blob> {
    const url = `${this.baseURL}/export/pdf/`;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRFToken': this.getCsrfToken(),
      },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const errorBody = await response.text().catch(() => '');
      throw new Error(`HTTP ${response.status}: ${errorBody}`);
    }
    return await response.blob();
  }

  // --- Validation ---

  async validate(schema: any): Promise<{ valid: boolean; errors: string[] }> {
    return this.request<{ valid: boolean; errors: string[] }>('/validate/', {
      method: 'POST',
      body: JSON.stringify(schema),
    });
  }

  // --- Circuit assistant ---

  async getAgentStatus(): Promise<AgentStatus> {
    return this.request<AgentStatus>('/agent/status/');
  }

  /**
   * One assistant turn. The backend runs it as a job (a turn can outlast proxy
   * timeouts); this starts the job and polls it until it finishes, reporting
   * each progress step. Errors carry the server's message (provider out of
   * credits, rate limit, …) rather than the raw HTTP body.
   */
  async agentChat(
    payload: AgentChatRequest,
    onProgress?: (steps: string[], elapsed: number) => void,
    options: { pollMs?: number; maxWaitMs?: number } = {},
  ): Promise<AgentChatResponse> {
    const pollMs = options.pollMs ?? 1000;
    const maxWaitMs = options.maxWaitMs ?? 5 * 60 * 1000;

    const response = await fetch(`${this.baseURL}/agent/chat/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRFToken': this.getCsrfToken(),
      },
      credentials: 'include',
      body: JSON.stringify(payload),
    });
    let job: AgentJobState | null = await response.json().catch(() => null);
    if (!response.ok) {
      const detail = (job as any)?.error || (job as any)?.detail;
      if (response.status === 429) {
        throw new Error(detail || 'Too many assistant requests — please wait a moment.');
      }
      throw new Error(detail || `The assistant failed (HTTP ${response.status}).`);
    }

    const started = Date.now();
    let networkErrors = 0;
    while (job && job.status === 'running') {
      onProgress?.(job.progress, job.elapsed);
      if (Date.now() - started > maxWaitMs) {
        throw new Error('The assistant is taking too long. Please try again.');
      }
      await new Promise(resolve => setTimeout(resolve, pollMs));
      try {
        const poll = await fetch(`${this.baseURL}/agent/jobs/${job.job_id}/`, { credentials: 'include' });
        if (poll.status === 429) {
          // Rate-limited by something in front of the backend: slow down, keep waiting.
          await new Promise(resolve => setTimeout(resolve, pollMs * 3));
          continue;
        }
        const body = await poll.json().catch(() => null);
        if (!poll.ok) {
          throw new Error(body?.error || `The assistant failed (HTTP ${poll.status}).`);
        }
        job = body as AgentJobState;
        networkErrors = 0;
      } catch (err) {
        // A dropped poll (network blip, proxy restart) is retried a few times.
        networkErrors += 1;
        if (networkErrors > 5) throw err;
      }
    }
    if (!job) throw new Error('The assistant returned no answer.');
    if (job.status === 'error') throw new Error(job.error || 'The assistant failed.');
    onProgress?.(job.progress, job.elapsed);
    return job.result as AgentChatResponse;
  }

  // --- Circuit Sharing ---

  async shareCircuit(data: { title: string; circuit_schema: any }): Promise<{ share_id: string; share_url: string }> {
    return this.request<{ share_id: string; share_url: string }>('/circuits/share/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async loadSharedCircuit(shareId: string): Promise<{ id: number; share_id: string; title: string; circuit_schema: any }> {
    return this.request<any>(`/circuits/shared/${shareId}/`);
  }

  // --- Analytics ---

  async trackEvent(eventType: string, metadata: Record<string, any> = {}): Promise<void> {
    try {
      await this.request<any>('/analytics/track/', {
        method: 'POST',
        body: JSON.stringify({ event_type: eventType, metadata }),
      });
    } catch {
      // Analytics should never break the main flow
    }
  }

  async getAnalyticsSummary(): Promise<AnalyticsSummary> {
    return this.request<AnalyticsSummary>('/analytics/summary/');
  }

  // --- Auth ---

  async authMe(): Promise<AuthResponse> {
    return this.request<AuthResponse>('/auth/me/');
  }

  async authRegister(data: {
    email: string;
    password: string;
    first_name?: string;
    last_name?: string;
    company?: string;
    phone?: string;
    job_title?: string;
  }): Promise<AuthResponse> {
    return this.request<AuthResponse>('/auth/register/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async authLogin(username: string, password: string): Promise<AuthResponse> {
    return this.request<AuthResponse>('/auth/login/', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    });
  }

  async authLogout(): Promise<void> {
    await this.request<any>('/auth/logout/', { method: 'POST' });
  }

  async authUpdateMe(data: {
    first_name?: string;
    last_name?: string;
    company?: string;
    phone?: string;
    job_title?: string;
    preferred_units?: string;
    onboarding_completed?: boolean;
  }): Promise<AuthResponse> {
    return this.request<AuthResponse>('/auth/me/', {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async authChangePassword(currentPassword: string, newPassword: string): Promise<void> {
    await this.request<any>('/auth/password/change/', {
      method: 'POST',
      body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
    });
  }

  async authResetPassword(email: string): Promise<void> {
    await this.request<any>('/auth/password/reset/', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  async authResetPasswordConfirm(token: string, newPassword: string): Promise<void> {
    await this.request<any>('/auth/password/reset/confirm/', {
      method: 'POST',
      body: JSON.stringify({ token, new_password: newPassword }),
    });
  }

  async authVerifyEmail(token: string): Promise<void> {
    await this.request<any>('/auth/verify-email/', {
      method: 'POST',
      body: JSON.stringify({ token }),
    });
  }

  // --- Projects ---

  async getProjects(includeArchived?: boolean): Promise<Project[]> {
    const qs = includeArchived ? '?include_archived=true' : '';
    return this.request<Project[]>(`/projects/${qs}`);
  }

  async getProject(id: number): Promise<Project> {
    return this.request<Project>(`/projects/${id}/`);
  }

  async createProject(data: { name: string; description?: string }): Promise<Project> {
    return this.request<Project>('/projects/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateProject(id: number, data: Partial<Project>): Promise<Project> {
    return this.request<Project>(`/projects/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async archiveProject(id: number): Promise<void> {
    await this.request<any>(`/projects/${id}/archive/`, { method: 'POST' });
  }

  async restoreProject(id: number): Promise<void> {
    await this.request<any>(`/projects/${id}/restore/`, { method: 'POST' });
  }

  async deleteProject(id: number): Promise<void> {
    await this.request(`/projects/${id}/`, { method: 'DELETE' });
  }

  // --- User Circuits (cloud save/load) ---

  async getCircuits(params?: { project?: number; ordering?: string }): Promise<SavedCircuit[]> {
    const query = new URLSearchParams();
    if (params?.project) query.set('project', String(params.project));
    if (params?.ordering) query.set('ordering', params.ordering);
    const qs = query.toString();
    return this.request<SavedCircuit[]>(`/circuits/${qs ? '?' + qs : ''}`);
  }

  async getCircuit(id: number): Promise<SavedCircuit> {
    return this.request<SavedCircuit>(`/circuits/${id}/`);
  }

  async saveCircuit(data: {
    title: string;
    circuit_schema: any;
    project?: number;
    description?: string;
  }): Promise<SavedCircuit> {
    return this.request<SavedCircuit>('/circuits/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateCircuit(id: number, data: Partial<SavedCircuit>): Promise<SavedCircuit> {
    return this.request<SavedCircuit>(`/circuits/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteCircuit(id: number): Promise<void> {
    await this.request(`/circuits/${id}/`, { method: 'DELETE' });
  }

  async duplicateCircuit(id: number, projectId?: number): Promise<SavedCircuit> {
    return this.request<SavedCircuit>(`/circuits/${id}/duplicate/`, {
      method: 'POST',
      body: JSON.stringify({ project_id: projectId }),
    });
  }

  async toggleCircuitPublic(id: number): Promise<{ is_public: boolean }> {
    return this.request<{ is_public: boolean }>(`/circuits/${id}/toggle-public/`, {
      method: 'POST',
    });
  }

  async shareCircuitWithUser(circuitId: number, email: string, permission: string): Promise<CircuitShareRecord> {
    return this.request<CircuitShareRecord>(`/circuits/${circuitId}/share/`, {
      method: 'POST',
      body: JSON.stringify({ email, permission }),
    });
  }

  async getCircuitShares(circuitId: number): Promise<CircuitShareRecord[]> {
    return this.request<CircuitShareRecord[]>(`/circuits/${circuitId}/share/`);
  }

  async getSharedWithMe(): Promise<SavedCircuit[]> {
    return this.request<SavedCircuit[]>('/circuits/shared-with-me/');
  }

  // --- User Components ---

  async getUserComponents(type?: string): Promise<UserComponent[]> {
    const qs = type ? `?type=${type}` : '';
    return this.request<UserComponent[]>(`/user-components/${qs}`);
  }

  async getUserComponent(id: number): Promise<UserComponent> {
    return this.request<UserComponent>(`/user-components/${id}/`);
  }

  async createUserComponent(data: Partial<UserComponent>): Promise<UserComponent> {
    return this.request<UserComponent>('/user-components/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateUserComponent(id: number, data: Partial<UserComponent>): Promise<UserComponent> {
    return this.request<UserComponent>(`/user-components/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteUserComponent(id: number): Promise<void> {
    await this.request(`/user-components/${id}/`, { method: 'DELETE' });
  }

  async uploadUserComponentCurve(id: number, file: File): Promise<any> {
    const formData = new FormData();
    formData.append('file', file);
    const url = `${this.baseURL}/user-components/${id}/upload-curve/`;
    const response = await fetch(url, {
      method: 'POST',
      body: formData,
      credentials: 'include',
      headers: { 'X-CSRFToken': this.getCsrfToken() },
    });
    if (!response.ok) {
      const errorBody = await response.text().catch(() => '');
      throw new Error(`HTTP ${response.status}: ${errorBody}`);
    }
    return await response.json();
  }

  // --- Simulation History ---

  async getSimulationRuns(circuitId?: number): Promise<SimulationRunRecord[]> {
    const qs = circuitId ? `?circuit=${circuitId}` : '';
    return this.request<SimulationRunRecord[]>(`/simulations/${qs}`);
  }

  async getSimulationRun(id: number): Promise<SimulationRunRecord> {
    return this.request<SimulationRunRecord>(`/simulations/${id}/`);
  }

  async deleteSimulationRun(id: number): Promise<void> {
    await this.request(`/simulations/${id}/`, { method: 'DELETE' });
  }

  // --- Admin User Management ---

  async createAdminUser(data: { username: string; email: string; password: string; company?: string }): Promise<AdminUser> {
    return this.request<AdminUser>('/admin-api/users/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getAdminUsers(search?: string): Promise<AdminUser[]> {
    const qs = search ? `?search=${encodeURIComponent(search)}` : '';
    return this.request<AdminUser[]>(`/admin-api/users/${qs}`);
  }

  async getAdminUser(id: number): Promise<AdminUser> {
    return this.request<AdminUser>(`/admin-api/users/${id}/`);
  }

  async updateAdminUser(id: number, data: Partial<AdminUser>): Promise<AdminUser> {
    return this.request<AdminUser>(`/admin-api/users/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async adminSetPassword(userId: number, password: string): Promise<void> {
    await this.request<any>(`/admin-api/users/${userId}/set-password/`, {
      method: 'POST',
      body: JSON.stringify({ password }),
    });
  }

  async deleteAdminUser(id: number): Promise<void> {
    await this.request(`/admin-api/users/${id}/`, { method: 'DELETE' });
  }

  async getAdminUserComponents(userId: number): Promise<UserComponent[]> {
    return this.request<UserComponent[]>(`/admin-api/users/${userId}/components/`);
  }

  async getAdminUserActivity(userId: number): Promise<any> {
    return this.request<any>(`/admin-api/users/${userId}/activity/`);
  }
}

export const apiService = new ApiService();
